import type { AppRoutes } from "@/Routers/AppRouter";
import type { RouteDecorators } from "@/pluginTypes";

interface RouteDecoratorSource {
  isLoading: boolean;
  routeDecorators?: RouteDecorators;
}

/** Same conflict policy as plugin routes: the last loaded plugin wins. */
export function collectRouteDecorators(
  plugins: readonly RouteDecoratorSource[],
): RouteDecorators {
  return plugins.reduce<RouteDecorators>(
    (decorators, plugin) =>
      plugin.isLoading
        ? decorators
        : { ...decorators, ...plugin.routeDecorators },
    {},
  );
}

/** Decorate existing exact route keys without adding keys or changing order.
 * Apply before plugin route replacement, so replacement pages own their layouts.
 * Calling the decorator directly preserves the returned element's identity.
 */
export function decorateRoutes(
  routes: AppRoutes,
  decorators: RouteDecorators,
): AppRoutes {
  if (Object.keys(decorators).length === 0) return routes;
  const result = { ...routes };
  for (const key of Object.keys(routes)) {
    if (!Object.hasOwn(decorators, key)) continue;
    const render = routes[key];
    const decorate = decorators[key];
    result[key] = (params) => decorate(render(params), params);
  }
  return result;
}
